import { useState, useEffect, useCallback } from "react";
import { getWithExpiry, setWithExpiry } from "@/utils/token";

export interface FetchResult<T> {
  fetchData: null | T;
  loading: boolean;
  error: Error | null;
}

interface FetchResultRun<T, P extends Record<string, any> = {}>
  extends FetchResult<T> {
  executeFetch: keyof P extends never
    ? (params?: P) => Promise<T>
    : (params: P) => Promise<T>;
}
export interface FetchOptions<T> {
  method?: string;
  url: string;
  body?: T;
  headers?: Record<string, string>;
}

async function callFetch<T>(
  options: FetchOptions<T>
): Promise<{ data?: T; error?: Error }> {
  try {
    const { method, url, body, headers } = options;
    const METHOD = method?.toUpperCase() ?? "GET";
    const fullUrl =
      METHOD === "GET" && body
        ? `${url}?${new URLSearchParams(body as any)}`
        : `${url}`;

    const header = new Headers({
      "Content-Type": "application/json",
      Authorization: `${getWithExpiry("token")}`,
      ...headers,
    });

    // fetch에서 Content-Type이 multipart/form-data일 경우 Content-Type을 삭제해야 정상처리됨.
    if (header.get("Content-Type") == "multipart/form-data")
      header.delete("Content-Type");

    let bodyData =
      JSON.stringify(body) == "{}" ? (body as FormData) : JSON.stringify(body);

    const config: RequestInit = {
      method: METHOD,
      headers: header,
      body: METHOD !== "GET" ? bodyData : null,
    };

    const response = await fetch(fullUrl, config);

    if (!response.ok) {
      if (response.status === 401) {
        localStorage.removeItem("token");
        window.location.href = "/login";
        throw new Error("Authentication error");
      }
      const errorData = await response.text();
      try {
        const errorJson = JSON.parse(errorData);
        return { data: errorJson };
      } catch (error) {
        return { error: error as Error };
      }
    }

    const token = response.headers.get("token");
    if (token) setWithExpiry("token", token);

    const data: T = await response.json();
    return { data };
  } catch (error) {
    return { error: error as Error };
  }
}
function useFetch<T>(props: FetchOptions<any>): FetchResult<T> {
  const [fetchData, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    setLoading(true);
    callFetch(props)
      .then((data) => {
        setData(data.data ?? null);
        setLoading(false);
      })
      .catch((error) => {
        setError(error);
        setLoading(false);
      });
    return () => setData(null); // Cleanup
  }, [JSON.stringify(props.body), props.url]); // Listen to changes in `props` to re-fetch data

  return { fetchData, loading, error };
}

function useFetchRun<T, P extends Record<string, any> = {}>(
  props: FetchOptions<any>
): FetchResultRun<T, P> {
  const [fetchData, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  const executeFetch = useCallback(
    async (params?: P) => {
      setLoading(true);
      try {
        const data = await callFetch({ ...props, ...params });
        setData(data.data ?? null);
        return data.data;
      } catch (error) {
        setError(error as Error);
      } finally {
        setLoading(false);
      }
    },
    [props]
  );

  return { fetchData, loading, error, executeFetch };
}

function useMultiFetch<T>(options: FetchOptions<T>[]): FetchResult<any>[] {
  const [results, setResults] = useState<FetchResult<T>[]>(
    options.map(() => ({
      fetchData: null,
      loading: true,
      error: null,
    }))
  );

  useEffect(() => {
    Promise.all(options.map((option) => callFetch(option)))
      .then((dataArray) => {
        const newResults = dataArray.map((item) => ({
          fetchData: item.data ?? null,
          error: item.error ?? null,
          loading: false,
        }));
        setResults(newResults);
      })
      .catch((error) => {
        setResults(
          results.map(() => ({
            fetchData: null,
            loading: false,
            error: error,
          }))
        );
      });
  }, [options]);

  return results;
}

export interface ApiResponse<T> {
  meta: {
    code: number;
    message: string;
  };
  data: T;
}

export { useFetch, useMultiFetch, useFetchRun };
